import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FilePlus2, Package, ReceiptText, UploadCloud } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import { apiService } from '../../services/api';
import { useNexusRefresh } from '../../context/NexusSyncContext';
import { useAuth } from '../Auth';
import ExpenseForm from '../ExpenseManagement/ExpenseForm';
import Inbox from './Inbox';
import InvoiceDrawer from './InvoiceDrawer';
import InvoiceList from './InvoiceList';
import ProjectPurchases from './ProjectPurchases';
import { ProjectSelect, SearchBox } from './ui';

// Dépenses : déposer des factures (lues par l'IA, réparties en une phrase), les factures enregistrées (compactes,
// par mois) et les achats par projet. La saisie manuelle (ancien formulaire) reste disponible.

const PERIODS = [
  { key: 'all', label: 'Tout' },
  { key: 'month', label: 'Ce mois' },
  { key: 'quarter', label: '3 mois' },
  { key: 'year', label: 'Cette année' },
];

const periodStart = (key) => {
  const now = new Date();
  if (key === 'month') return new Date(now.getFullYear(), now.getMonth(), 1);
  if (key === 'quarter') return new Date(now.getFullYear(), now.getMonth() - 2, 1);
  if (key === 'year') return new Date(now.getFullYear(), 0, 1);
  return null;
};

const isoDay = (date) => (date ? `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}` : null);

const ExpensesPage = () => {
  const { user } = useAuth();
  const isAdmin = Boolean(user?.is_admin) || Number(user?.employee_id ?? user?.id) === 1;
  const employeeId = user?.employee_id ?? null;
  const [tab, setTab] = useState('invoices');
  const [search, setSearch] = useState('');
  const [projectId, setProjectId] = useState(null);
  const [period, setPeriod] = useState('all');
  const [person, setPerson] = useState('');
  const [projects, setProjects] = useState([]);
  const [people, setPeople] = useState([]);
  const [list, setList] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [manual, setManual] = useState(null);
  const [notice, setNotice] = useState(null);
  const [dragging, setDragging] = useState(false);
  const inbox = useRef(null);
  const dragDepth = useRef(0);
  const legacyProjects = useRef(null);
  const [params, setParams] = useSearchParams();

  useEffect(() => {
    apiService.getProjectOptions().then((rows) => setProjects(rows || [])).catch(() => setProjects([]));
    apiService.get('/employees').then((rows) => setPeople((rows || []).filter((p) => p.is_active !== false)
      .map((p) => ({ id: p.id, name: p.name })).sort((a, b) => a.name.localeCompare(b.name)))).catch(() => setPeople([]));
  }, []);

  const query = useMemo(() => {
    const params = new URLSearchParams();
    if (search.trim()) params.set('q', search.trim());
    if (projectId) params.set('project_id', projectId);
    if (person && isAdmin) params.set('employee_id', person);
    const start = periodStart(period);
    if (start) params.set('date_from', isoDay(start));
    return params;
  }, [search, projectId, person, period, isAdmin]);

  const load = useCallback(async (append = false) => {
    setLoading(true);
    try {
      const params = new URLSearchParams(query);
      params.set('offset', append ? String(list.items.length) : '0');
      params.set('limit', '50');
      const data = await apiService.get(`/purchases/invoices?${params.toString()}`);
      setList((current) => ({ items: append ? [...current.items, ...data.items] : data.items, total: data.total }));
    } catch (err) {
      setNotice({ tone: 'error', text: err.message });
    } finally {
      setLoading(false);
    }
  }, [query, list.items.length]);

  // Recharge quand les filtres changent (recherche : après une courte pause de frappe).
  useEffect(() => {
    const timer = setTimeout(() => load(false), search ? 300 : 0);
    return () => clearTimeout(timer);
  }, [query]); // eslint-disable-line react-hooks/exhaustive-deps

  useNexusRefresh(['expenses'], () => load(false));

  useEffect(() => {
    if (!notice) return undefined;
    const timer = setTimeout(() => setNotice(null), 5000);
    return () => clearTimeout(timer);
  }, [notice]);

  const loadLegacyProjects = useCallback(async () => {
    if (!legacyProjects.current) legacyProjects.current = await apiService.get('/projects?include_all=true').catch(() => []);
    return legacyProjects.current;
  }, []);

  const openInvoiceById = useCallback(async (expenseId) => {
    const found = list.items.find((item) => item.id === expenseId);
    if (found) { setSelected(found); return; }
    try {
      const e = await apiService.get(`/expenses/${expenseId}`);
      setSelected({
        id: e.id, date: (e.date || '').slice(0, 10), vendor: e.vendor, description: e.description, amount: e.amount, tva: e.tva,
        currency: e.currency || '€', employee_name: e.employee_name, pennylane_status: e.pennylane_status,
        file_url: e.file_path ? `/expenses/files/${e.file_path.split('/').pop()}` : null,
      });
    } catch (err) {
      setNotice({ tone: 'error', text: err.message });
    }
  }, [list.items]);

  // Lien direct vers une facture (fiche projet, Nexus) : /expenses?facture=ID
  useEffect(() => {
    const wanted = Number(params.get('facture'));
    if (!wanted) return;
    openInvoiceById(wanted);
    params.delete('facture');
    setParams(params, { replace: true });
  }, [params]); // eslint-disable-line react-hooks/exhaustive-deps

  const onValidated = useCallback(() => {
    setNotice({ tone: 'ok', text: 'Facture enregistrée et répartie sur ses projets.' });
    load(false);
  }, [load]);

  const openManual = async () => setManual({ projects: await loadLegacyProjects() });
  const saveManual = async (formData) => {
    await apiService.uploadFile('/expenses', formData);
    setManual(null);
    setNotice({ tone: 'ok', text: 'Dépense ajoutée.' });
    load(false);
  };

  // Glisser-déposer n'importe où sur la page.
  const onDragEnter = (e) => {
    if (!Array.from(e.dataTransfer?.types || []).includes('Files')) return;
    e.preventDefault();
    dragDepth.current += 1;
    setDragging(true);
  };
  const onDragLeave = (e) => {
    e.preventDefault();
    dragDepth.current = Math.max(0, dragDepth.current - 1);
    if (!dragDepth.current) setDragging(false);
  };
  const onDrop = (e) => {
    if (!e.dataTransfer?.files?.length) return;
    e.preventDefault();
    dragDepth.current = 0;
    setDragging(false);
    inbox.current?.upload(e.dataTransfer.files);
  };

  return (
    <div className="relative min-h-screen bg-gray-50/60" onDragEnter={onDragEnter} onDragOver={(e) => e.preventDefault()}
      onDragLeave={onDragLeave} onDrop={onDrop}>
      <div className="mx-auto max-w-6xl space-y-5 px-4 py-5 md:px-6">
        <header className="flex flex-wrap items-center gap-3">
          <h1 className="text-xl font-semibold text-gray-900">Dépenses</h1>
          <div className="flex rounded-lg border border-gray-200 bg-white p-0.5 shadow-sm">
            {[{ key: 'invoices', label: 'Factures', icon: ReceiptText }, { key: 'purchases', label: 'Achats par projet', icon: Package }]
              .map(({ key, label, icon: Icon }) => (
                <button key={key} type="button" onClick={() => setTab(key)}
                  className={`inline-flex items-center gap-1.5 rounded-md px-3 py-1 text-sm ${tab === key ? 'bg-gray-900 text-white' : 'text-gray-600 hover:text-gray-900'}`}>
                  <Icon className="h-4 w-4" /> {label}
                </button>
              ))}
          </div>
          <SearchBox value={search} onChange={setSearch} className="min-w-[12rem] flex-1 md:max-w-sm"
            placeholder={tab === 'invoices' ? 'Fournisseur, article, référence…' : 'Chercher un article dans tous les projets…'} />
          <button type="button" onClick={openManual}
            className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-sm text-gray-700 shadow-sm hover:bg-gray-50">
            <FilePlus2 className="h-4 w-4" /> Saisie manuelle
          </button>
        </header>


        <Inbox ref={inbox} projects={projects} people={people} isAdmin={isAdmin} employeeId={employeeId}
          onValidated={onValidated} onOpenInvoice={openInvoiceById} />

        {tab === 'invoices' ? (
          <section className="space-y-3">
            <div className="flex flex-wrap items-center gap-2">
              <ProjectSelect projects={projects} value={projectId} onChange={setProjectId} placeholder="Tous les projets" className="w-56" />
              {projectId && <button type="button" onClick={() => setProjectId(null)} className="text-xs text-gray-500 hover:underline">tous</button>}
              <div className="flex rounded-lg border border-gray-200 bg-white p-0.5">
                {PERIODS.map((p) => (
                  <button key={p.key} type="button" onClick={() => setPeriod(p.key)}
                    className={`rounded-md px-2.5 py-0.5 text-xs ${period === p.key ? 'bg-gray-100 font-medium text-gray-900' : 'text-gray-500 hover:text-gray-800'}`}>
                    {p.label}
                  </button>
                ))}
              </div>
              {isAdmin && (
                <select value={person} onChange={(e) => setPerson(e.target.value)}
                  className="rounded-lg border border-gray-200 bg-white px-2 py-1 text-xs text-gray-700">
                  <option value="">Tout le monde</option>
                  {people.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
              )}
            </div>
            <InvoiceList items={list.items} total={list.total} loading={loading} selectedId={selected?.id}
              onOpen={setSelected} onMore={() => load(true)} />
          </section>
        ) : (
          <ProjectPurchases search={search} onOpenInvoice={openInvoiceById} />
        )}
      </div>

      {notice && (
        <div role="status" className={`fixed bottom-5 left-1/2 z-50 -translate-x-1/2 rounded-lg px-4 py-2 text-sm shadow-lg ring-1
          ${notice.tone === 'ok' ? 'bg-emerald-600 text-white ring-emerald-700' : 'bg-red-600 text-white ring-red-700'}`}>
          {notice.text}
        </div>
      )}

      {dragging && (
        <div className="pointer-events-none fixed inset-0 z-50 flex items-center justify-center bg-blue-600/10 backdrop-blur-[1px]">
          <div className="rounded-2xl border-2 border-dashed border-blue-400 bg-white px-10 py-8 text-center shadow-xl">
            <UploadCloud className="mx-auto h-10 w-10 text-blue-500" />
            <p className="mt-2 text-base font-semibold text-gray-900">Dépose tes factures</p>
            <p className="text-sm text-gray-500">Elles seront lues et réparties ; tu valides d'un clic.</p>
          </div>
        </div>
      )}

      {selected && (
        <InvoiceDrawer invoice={selected} projects={projects} employeeId={employeeId} loadLegacyProjects={loadLegacyProjects}
          onClose={() => setSelected(null)} onChanged={() => load(false)}
          onDeleted={() => { setSelected(null); setNotice({ tone: 'ok', text: 'Dépense supprimée.' }); load(false); }} />
      )}

      {manual && (
        <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-gray-900/40 p-4">
          <div className="w-full max-w-4xl rounded-xl bg-white shadow-2xl">
            <ExpenseForm onSubmit={saveManual} projects={manual.projects} employeeId={employeeId}
              onCancel={() => setManual(null)} apiBaseUrl={apiService.getBaseUrl()} />
          </div>
        </div>
      )}
    </div>
  );
};

export default ExpensesPage;
